import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  MenuItem,
  IconButton,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import useAdd from "../../api/useAdd";
import useFetchObjects from "../../api/useFetchObjects";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";

const PurchaseAdd = ({ open, onClose, token }) => {
  const { t } = useTranslation();
  const { handleAdd, loading, resetForm } = useAdd("shop/purchases", token);

  const { data: shopsData, loading: shopLoading } = useFetchObjects(
    ["shop"],
    "shop/shop?page_size=all",
    token
  );
  const { data: suppliersData, loading: supplierLoading } = useFetchObjects(
    ["supplier"],
    "shop/suppliers?page_size=all", 
    token
  );
  const { data: productsData, loading: productLoading } = useFetchObjects(
    ["product"],
    "shop/products?page_size=all",
    token
  );
  const { data: unitsData, loading: unitLoading } = useFetchObjects(
    ["unit"],
    "shop/units?page_size=all",
    token
  );
  const { data: currenciesData, loading: currencyLoading } = useFetchObjects(
    ["currency"],
    "shop/currencies?page_size=all",
    token
  );

  const shops = shopsData?.results || shopsData || [];
  const suppliers = suppliersData?.results || suppliersData || [];
  const products = productsData?.results || productsData || [];
  const units = unitsData?.results || unitsData || [];
  const currencies = currenciesData?.results || currenciesData || [];

  const [shop, setShop] = useState("");
  const [supplier, setSupplier] = useState("");
  const [discount, setDiscount] = useState("");
  const [receivedAmount, setReceivedAmount] = useState("");
  const [purchaseCurrency, setPurchaseCurrency] = useState("");
  const [purchaseDate, setPurchaseDate] = useState("");
  const [items, setItems] = useState([]);
  const [formError, setFormError] = useState("");

  const addItem = () => {
    setItems([
      ...items,
      {
        product: "",
        unit: "",
        quantity: "",
        buy_price: "",
        sale_price: "",
        expire_date: "",
      },
    ]);
  };

  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const totalAmount = items.reduce((acc, item) => {
    const qty = parseFloat(item.quantity) || 0;
    const price = parseFloat(item.buy_price) || 0;
    return acc + qty * price;
  }, 0);

  const discountAmount = (totalAmount * (parseFloat(discount) || 0)) / 100;
  const finalTotal = totalAmount - discountAmount;

  const itemsValid =
    items.length > 0 &&
    items.every(
      (item) =>
        item.product &&
        item.unit &&
        item.quantity &&
        item.buy_price &&
        item.sale_price
    );

  const parsedReceived = parseFloat(receivedAmount) || 0;
  const receivedValid = parsedReceived <= finalTotal;

  const isSubmitDisabled = loading;

  const handleSubmit = () => {
    if (!shop) {
      setFormError(t("purchase.shopRequired") || "Shop is required");
      return;
    }
    if (!supplier) {
      setFormError(t("purchase.supplierRequired") || "Supplier is required");
      return;
    }
    if (!purchaseCurrency) {
      setFormError(t("purchase.currencyRequired") || "Currency is required");
      return;
    }
    if (items.length === 0) {
      setFormError(
        t("purchase.atLeastOneItemRequired") ||
          "At least one purchase item is required"
      );
      return;
    }
    if (!itemsValid) {
      setFormError(
        t("purchase.completeItemFields") ||
          "Please complete all required fields for each purchase item"
      );
      return;
    }
    if (!receivedValid) {
      setFormError(
        t("purchase.receivedExceedsTotal") ||
          "Received amount should not exceed total amount"
      );
      return;
    }
    setFormError("");
    const payload = {
      shop,
      supplier,
      discount: discountAmount,
      received_amount: receivedAmount,
      currency: purchaseCurrency,
      purchase_date: purchaseDate || new Date().toISOString(),
      items,
    };
    handleAdd(payload);
  };

  useEffect(() => {
    if (resetForm) {
      setShop("");
      setSupplier("");
      setDiscount("");
      setReceivedAmount("");
      setPurchaseCurrency("");
      setPurchaseDate("");
      setItems([]);
      setFormError("");
      onClose();
    }
  }, [resetForm, onClose]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>{t("purchase.addPurchase")}</DialogTitle>
      <DialogContent>
        {formError && (
          <Typography color="error" sx={{ mb: 2 }}>
            {formError}
          </Typography>
        )}
        <Box
          component="form"
          noValidate
          sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 2 }}
        >
          <TextField
            select
            label={t("purchase.selectShop")}
            fullWidth
            required
            value={shop}
            onChange={(e) => setShop(e.target.value)}
            disabled={shopLoading}
          >
            {(shops || []).map((s) => (
              <MenuItem key={s.id} value={String(s.id)}>
                {s.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={t("purchase.selectSupplier")}
            fullWidth
            required
            value={supplier}
            onChange={(e) => setSupplier(e.target.value)}
            disabled={supplierLoading}
          >
            {(suppliers || []).map((sup) => (
              <MenuItem key={sup.id} value={String(sup.id)}>
                {sup.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={t("purchase.selectCurrency")}
            fullWidth
            required
            value={purchaseCurrency}
            onChange={(e) => setPurchaseCurrency(e.target.value)}
            disabled={currencyLoading}
          >
            {(currencies || []).map((curr) => (
              <MenuItem key={curr.id} value={String(curr.id)}>
                {curr.title}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label={t("purchase.purchaseDate")}
            type="date"
            fullWidth
            required
            value={purchaseDate}
            onChange={(e) => setPurchaseDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label={`${t("purchase.discount")} %`}
            type="number"
            fullWidth
            value={discount}
            onChange={(e) => setDiscount(e.target.value)}
          />
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6">{t("purchase.purchaseItems")}</Typography>
            {items.map((item, index) => (
              <Box
                key={index}
                sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}
              >
                <TextField
                  select
                  label={t("purchase.selectProduct")}
                  required
                  value={item.product}
                  onChange={(e) =>
                    handleItemChange(index, "product", e.target.value)
                  }
                  sx={{ flex: 1 }}
                  disabled={productLoading}
                >
                  {(products || []).map((prod) => (
                    <MenuItem key={prod.id} value={String(prod.id)}>
                      {prod.name}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label={t("purchase.selectUnit")}
                  required
                  value={item.unit}
                  onChange={(e) =>
                    handleItemChange(index, "unit", e.target.value)
                  }
                  sx={{ flex: 1 }}
                  disabled={unitLoading}
                >
                  {(units || []).map((unit) => (
                    <MenuItem key={unit.id} value={String(unit.id)}>
                      {unit.title}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label={t("purchase.quantity")}
                  type="number"
                  required
                  value={item.quantity}
                  onChange={(e) =>
                    handleItemChange(index, "quantity", e.target.value)
                  }
                  sx={{ flex: 1 }}
                />
                <TextField
                  label={t("purchase.buyPrice")}
                  type="number"
                  required
                  value={item.buy_price}
                  onChange={(e) =>
                    handleItemChange(index, "buy_price", e.target.value)
                  }
                  sx={{ flex: 1 }}
                />
                <TextField
                  label={t("purchase.salePrice")}
                  type="number"
                  required
                  value={item.sale_price}
                  onChange={(e) =>
                    handleItemChange(index, "sale_price", e.target.value)
                  }
                  sx={{ flex: 1 }}
                />
                <TextField
                  label={t("purchase.expireDate")}
                  type="date"
                  fullWidth
                  value={item.expire_date}
                  onChange={(e) =>
                    handleItemChange(index, "expire_date", e.target.value)
                  }
                  InputLabelProps={{ shrink: true }}
                  sx={{ flex: 1 }}
                />
                <IconButton onClick={() => removeItem(index)} color="error">
                  <RemoveCircleOutlineIcon />
                </IconButton>
              </Box>
            ))}
            <Button
              variant="outlined"
              startIcon={<AddCircleOutlineIcon />}
              onClick={addItem}
              sx={{ mt: 1 }}
            >
              {t("purchase.addItem")}
            </Button>
          </Box>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1">
              {t("purchase.subtotal")}: {totalAmount.toFixed(2)}
            </Typography>
            {discountAmount > 0 && (
              <Typography variant="body1">
                {t("purchase.discountAmount")}: -{discountAmount.toFixed(2)}
              </Typography>
            )}
            <Typography variant="h6">
              {t("purchase.totalAmount")}: {finalTotal.toFixed(2)}
            </Typography>
          </Box>
          <TextField
            label={t("purchase.receivedAmount")}
            type="number"
            fullWidth
            value={receivedAmount}
            onChange={(e) => setReceivedAmount(e.target.value)}
            error={!receivedValid}
            helperText={
              !receivedValid ? t("purchase.receivedExceedsTotal") : ""
            }
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          {t("purchase.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={isSubmitDisabled}
          sx={{ minWidth: 100 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("purchase.submit")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PurchaseAdd;
